import React, { useState } from 'react';
import { Box, Button, Chip, Container, IconButton, ListItemIcon, ListItemText, MenuItem, Select, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Form from '@rjsf/mui';
import { RJSFSchema, WidgetProps } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import PrimaryLayout from '../../components/layout/PrimaryLayout';
import { scheduleWorkflow } from '../../services/workflow-service';

import XGBoostIcon from '@mui/icons-material/TrendingUp'; // Example icon
import SklearnIcon from '@mui/icons-material/Functions'; // Example icon
import H20Icon from '@mui/icons-material/Water'; // Example icon

type ModelType = 'xgboost' | 'sklearn' | 'h20';

const modelTypeIcons: Record<ModelType, JSX.Element> = {
  xgboost: <XGBoostIcon />,
  sklearn: <SklearnIcon />,
  h20: <H20Icon />,
};

// Custom widget to render the dropdown with icons
const CustomSelectWidget: React.FC<WidgetProps> = ({ value, options, onChange }) => {
  return (
    <Select
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      displayEmpty
      fullWidth
    >
      {options.enumOptions!.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <ListItemIcon>{modelTypeIcons[option.value as ModelType]}</ListItemIcon>
          <ListItemText primary={option.label} />
        </MenuItem>
      ))}
    </Select>
  );
};


// Styled components
const DropZone = styled('div')(({ theme }) => ({
  border: '2px dashed grey',
  borderRadius: '4px',
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  marginBottom: theme.spacing(2),
}));

const TagInput = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const AddModel = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [requestFile, setRequestFile] = useState<File | null>(null);
  const [responseFile, setResponseFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorRequestMessage, setErrorRequestMessage] = useState<string | null>(null);
  const [errorResponseMessage, setErrorResponseMessage] = useState<string | null>(null);
  const [base64FileData, setBase64FileData] = useState<string>('');
  const [tags, setTags] = useState<{ [key: string]: string }>({});
  const [keyInput, setKeyInput] = useState<string>('');
  const [valueInput, setValueInput] = useState<string>('');
  const MAX_FILE_SIZE = 1 * 1024 * 1024;

  interface FormData {
    name?: string;
    model_type?: string;
    taskqueue?: string;
    description?: string;
  }

  const [formData, setFormData] = useState<FormData>({});

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) handleFile(file);
  };

  const handleJsonDrop = (event: React.DragEvent<HTMLDivElement>, setFile: string) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) handleJsonFile(file, setFile);
  };

  const handleJsonFileChange = (event: React.ChangeEvent<HTMLInputElement>, setFile: string) => {
    const file = event.target.files?.[0] || null;
    if (file) handleJsonFile(file, setFile);
  };

  const handleFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage('File size exceeds the maximum limit of 1 MB.');
      setSelectedFile(null);
    } else {
      setErrorMessage(null);
      setSelectedFile(file);
      convertToBase64(file);
    }
  };

  const handleJsonFile = (file: File, setFile: string) => {
    if (file.size > MAX_FILE_SIZE) {
      setFile == "setRequestFile" ? setErrorRequestMessage('File size exceeds the maximum limit of 1 MB.') : setErrorResponseMessage('File size exceeds the maximum limit of 1 MB.');
      setFile == "setRequestFile" ? setRequestFile(null) : setResponseFile(null);
    } else if(file.type != "application/json") {
      setFile == "setRequestFile" ? setErrorRequestMessage('File should be of json format only') : setErrorResponseMessage('File should be of json format only');
      setFile == "setRequestFile" ? setRequestFile(null) : setResponseFile(null);
    } else {
      setErrorMessage(null);
      setFile == "setRequestFile" ? setRequestFile(file) : setResponseFile(file);
      // convertToBase64(file);
    }
  };

  const convertToBase64 = (file: File) => {
    try{
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const bytes = new Uint8Array(arrayBuffer);
        const binary = bytes.reduce((data, byte) => data + String.fromCharCode(byte), '');
        const base64Data = btoa(binary);
        setBase64FileData(base64Data);
      };
      reader.onerror = () => {
        console.error('Error reading file:', reader.error);
      };
      reader.readAsArrayBuffer(file);
  } catch (error) {
    console.error('Error occured while converting content to base64 format:', error);
  }
  };

  const convertToBase64ForJson = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        try {
          const text = reader.result as string;
          const jsonObject = JSON.parse(text);
          const jsonString = JSON.stringify(jsonObject);
          const base64Data = btoa(unescape(encodeURIComponent(jsonString)));
          resolve(base64Data);
        } catch (error) {
          console.error('Error converting file to base64:', error);
          reject('Error converting file to base64');
        }
      };
  
      reader.onerror = () => {
        console.error('Error reading file:', reader.error);
        reject('Error reading file');
      };
  
      reader.readAsText(file);
    });
  };  
  
  const handleTagAdd = () => {
    if (keyInput.trim() && valueInput.trim() && !(keyInput in tags)) {
      setTags({ ...tags, [keyInput]: valueInput });
      setKeyInput('');
      setValueInput('');
    }
  };

  const handleTagDelete = (keyToDelete: string) => () => {
    const newTags = { ...tags };
    delete newTags[keyToDelete];
    setTags(newTags);
  };

  const handleFormSubmit = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    try {
      if (selectedFile && formData && formData.name && formData.model_type) {
        let requestJsonBase64 = null, responseJsonBase64 = null;
        if (requestFile)
          requestJsonBase64 = await convertToBase64ForJson(requestFile);
        if (responseFile)
          responseJsonBase64 = await convertToBase64ForJson(responseFile);
        const tagsArray = Object.entries(tags).map(([key, value]) => ({ key, value }));
        const response = await scheduleWorkflow({
          artifact_file_path: selectedFile.name,
          artifact_data: base64FileData,
          mlflow_model_name: formData.name,
          model_type: formData.model_type ? formData.model_type : '',
          request_json: requestJsonBase64,
          response_json: responseJsonBase64,
          tags: tagsArray,
          description: formData.description || null
        });
  
        alert('Model uploaded and registered successfully!');
      } else {
        alert('Please select a file, provide a model name and model type');
      }
    } catch (error) {
      console.error('Error uploading model:', error);
      alert('Failed to upload and register model');
    }
  };
  
  const schema: RJSFSchema = {
    // title: "Upload Model",
    type: "object",
    properties: {
      name: { type: "string", title: "Model Name" },
      model_type: {
        type: "string",
        title: "Model Type",
        enum: ["xgboost", "sklearn", "h20", "lightgbm", "tensorflow", "keras"],
        // default: "xgboost",
      },
      description: { type: "string", title: "Model Description(Optional)" },
      // task_queue: { type: "string", title: "Task Queue Name" },
    },
    required: ["name", "model_type"]
  };

  const uiSchema = {
    model_type: {
      "ui:widget": CustomSelectWidget,
    },
    description: {
      "ui:widget": "textarea"
    }
  };

  return (
    <PrimaryLayout>
      <Container>
        <Typography variant="h4" sx={{ m: 2 }}>
          Add a new model
        </Typography>

        <Form schema={schema} uiSchema={uiSchema} formData={formData} onChange={(e) => setFormData(e.formData)} validator={validator}>
          <DropZone
            onDrop={handleDrop}
            onDragOver={handleDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <Typography>{selectedFile.name}</Typography>
            ) : (
              <Typography color={errorMessage ? "red" : "gray"}>
                Drag and drop a model file here, or click to select one (Note: File size maximum limit is 1 MB)
              </Typography>
            )}
          </DropZone>
          {errorMessage && (
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          )}
          <DropZone
            onDrop={(event) => {
              handleJsonDrop(event, "setRequestFile");
            }}
            onDragOver={(event) => {
              handleJsonDrop(event, "setRequestFile");
            }}
            onClick={() => document.getElementById('request-file-input')?.click()}
          >
            <input
              id="request-file-input"
              type="file"
              onChange={(event) => {
                handleJsonFileChange(event, "setRequestFile");
              }}
              style={{ display: 'none' }}
            />
            {requestFile ? (
              <Typography>{requestFile.name}</Typography>
            ) : (
              <Typography color={errorRequestMessage ? "red" : "gray"}>
                Drag and drop a sample data csv file here, or click to select one (Note: File size max limit is 1 MB)
              </Typography>
            )}
          </DropZone>
          {errorRequestMessage && (
            <Typography variant="body2" color="error">
              {errorRequestMessage}
            </Typography>
          )}
          

          <Box display="flex" flexWrap="wrap" mt={2}>
                  {Object.entries(tags).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${value}`}
                      onDelete={handleTagDelete(key)}
                      deleteIcon={<DeleteIcon />}
                      sx={{ margin: 0.5 }}
                    />
                  ))}
                </Box>

          <Typography variant="h6">
              Add Tag
          </Typography>
      <Box display="flex" alignItems="center">
        <TagInput
          label="Key"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
        />
        <TagInput
          label="Value"
          value={valueInput}
          onChange={(e) => setValueInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleTagAdd();
            }
          }}
        />
        <IconButton
        color="primary"
        sx={{
          '&:hover': {
            backgroundColor: 'lightblue',
          },
        }}
        onClick={handleTagAdd}
      >
        <AddIcon />
      </IconButton>
      </Box>

          <Button type="submit" variant="contained" color="primary" onClick={handleFormSubmit}>
            Upload Model
          </Button>
        </Form>
      </Container>
    </PrimaryLayout>
  );
};

export default AddModel;
